import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import {
  DEFAULT_LOCALE,
  detectPreferredLocale,
  getLocaleFromPath,
  isValidLocale,
  stripLocalePrefix,
  withLocalePath,
} from '@/lib/i18n-utils'
import { loadTranslationsForSSR } from '@/i18n'
import { buildLocalizedSeoHead, buildTopLevelBreadcrumbLd, jsonLd } from '@/lib/seo-head'

export const Route = createFileRoute('/{-$locale}')({
  beforeLoad: async ({ params, location, context }) => {
    const localeParam = params.locale
    const hasLocaleParam = typeof localeParam === 'string' && localeParam.length > 0
    const preferredLocale = detectPreferredLocale()
    const resolvedLocale = isValidLocale(localeParam) ? localeParam : preferredLocale

    // Skip locale redirect for auth callback — the redirect corrupts OAuth
    // search params (authorization code, state) via TanStack Router's
    // search serialization. The callback renders fine without a locale prefix.
    const normalizedPath = stripLocalePrefix(location.pathname)
    if (normalizedPath === '/auth/callback') {
      if (context.i18n.language !== resolvedLocale) {
        void context.i18n.changeLanguage(resolvedLocale)
      }
      return { locale: resolvedLocale }
    }

    const hash = typeof location.hash === 'string' ? location.hash : ''
    // Use raw browser search string to preserve unknown params (e.g. OAuth code, state)
    const rawSearch = typeof window !== 'undefined' ? window.location.search : ''
    const search = rawSearch
      ? Object.fromEntries(new URLSearchParams(rawSearch))
      : (location as any).search

    if (hasLocaleParam && !isValidLocale(localeParam)) {
      const restSegments = location.pathname.split('/').filter(Boolean).slice(1)
      const cleanPath = `/${restSegments.join('/')}`.replace(/\/+$/, '') || '/'
      const fallbackPath = withLocalePath(cleanPath, DEFAULT_LOCALE)
      throw redirect({ to: fallbackPath as any, search, hash, replace: true })
    }

    const canonicalPath = withLocalePath(normalizedPath, resolvedLocale)

    if (location.pathname !== canonicalPath) {
      throw redirect({ to: canonicalPath as any, search, hash, replace: true })
    }

    // Load translations for SSR (no-op on client — fetch backend handles it)
    await loadTranslationsForSSR(resolvedLocale)

    // Sync i18next language with URL
    if (context.i18n.language !== resolvedLocale) {
      await context.i18n.changeLanguage(resolvedLocale)
    }

    return { locale: resolvedLocale }
  },
  // No loader here on purpose: this route's own match only changes when its
  // `locale` param changes, so a loader keyed to this match would go stale on
  // a client-side navigation between sibling pages under the same locale
  // (e.g. /en-US/about -> /en-US/contact) — the match doesn't re-run and
  // head() would keep serving the old page's canonical/og:url/hreflang.
  // head() is invoked fresh on every navigation, so it derives everything
  // from the leaf match's own pathname instead.
  head: ({ matches }) => {
    if (matches[0]?.globalNotFound) return {}
    const leaf = matches[matches.length - 1]
    const pathname = leaf?.pathname ?? '/'
    const normalizedPath = stripLocalePrefix(pathname)
    const locale = getLocaleFromPath(pathname)
    const seoHead = buildLocalizedSeoHead(normalizedPath, locale)
    const breadcrumb = buildTopLevelBreadcrumbLd(normalizedPath, locale)
    return {
      meta: [
        { name: 'robots', content: seoHead.robots },
        { property: 'og:url', content: seoHead.canonicalUrl },
        { property: 'og:locale', content: seoHead.ogLocale },
      ],
      links: [
        { rel: 'canonical', href: seoHead.canonicalUrl },
        ...seoHead.alternates.map((alt) => ({ rel: 'alternate', hrefLang: alt.hreflang, href: alt.href })),
      ],
      scripts: breadcrumb ? [jsonLd(breadcrumb)] : [],
    }
  },
  component: () => <Outlet />,
})
